import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { collectionsApi } from "../services/collectionsApi";
import { apiService, Problem } from "../services/api";
import { Collection } from "../types/collection";
import { useAuth } from "../contexts/FirebaseAuthContext";
import { usePinManagement } from "../hooks/usePinManagement";
import { pinsApi, PinData } from "../services/pinsApi";
import LoadingModal from "../components/LoadingModal";
import "../styles/SequenceManagementPage.css";

interface ProblemWithSequence extends Problem {
  sequenceNumber: number;
}

type SortOption = 'difficulty-asc' | 'difficulty-desc' | 'leetcode' | 'completion' | 'custom';

const DIFFICULTY_ORDER: Record<string, number> = {
  'Easy': 1,
  'Medium': 2,
  'Hard': 3
};

// Helper to convert Firestore timestamp to Date
// Firestore timestamps from API come as {_seconds: number, _nanoseconds: number}
const formatFirestoreDate = (timestamp: unknown): string => {
  if (!timestamp) return '—';

  // Handle Firestore timestamp object
  if (typeof timestamp === 'object' && timestamp !== null && '_seconds' in timestamp) {
    const ts = timestamp as { _seconds: number; _nanoseconds?: number };
    return new Date(ts._seconds * 1000).toLocaleDateString();
  }

  // Handle regular Date or date string
  const date = new Date(timestamp as string | number | Date);
  if (!isNaN(date.getTime())) {
    return date.toLocaleDateString();
  }

  return '—';
};

export const SequenceManagementPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile, isLoading: authLoading } = useAuth();
  const { pinData } = usePinManagement();

  // Data state
  const [collection, setCollection] = useState<Collection | null>(null);
  const [pinnedProblems, setPinnedProblems] = useState<ProblemWithSequence[]>([]);
  const [unpinnedProblems, setUnpinnedProblems] = useState<ProblemWithSequence[]>([]);
  const [completionStatus, setCompletionStatus] = useState<Record<string, boolean>>({});
  const [originalSequence, setOriginalSequence] = useState<string[]>([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [dragEnabled, setDragEnabled] = useState(true);
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverItem, setDragOverItem] = useState<string | null>(null);

  // Load collection data
  useEffect(() => {
    const loadData = async () => {
      if (!id) return;

      // Wait for auth to complete before fetching user collections
      if (authLoading) {
        return;
      }

      try {
        setLoading(true);
        const response = await collectionsApi.getCollection(id);
        const collectionData = response.collection || response;
        setCollection(collectionData);

        // Load problems
        if (collectionData.problemIds && collectionData.problemIds.length > 0) {
          const allProblems = await apiService.getCollectionProblems(
            id,
            collectionData.problemIds,
            1,
            -1
          );

          // Determine the sequence order
          let sequence: string[];
          if (collectionData.problemSequence && collectionData.problemSequence.length > 0) {
            sequence = collectionData.problemSequence;
          } else {
            // Default: sort by difficulty (easy to hard)
            const sorted = [...allProblems].sort((a, b) => {
              return (DIFFICULTY_ORDER[a.difficulty] || 99) - (DIFFICULTY_ORDER[b.difficulty] || 99);
            });
            sequence = sorted.map(p => p.id);
          }

          setOriginalSequence([...sequence]);

          // Split into pinned and unpinned
          const collectionPins = pinData.pinnedProblems[id] || [];
          const pinned: ProblemWithSequence[] = [];
          const unpinned: ProblemWithSequence[] = [];

          // Create a map for quick lookup
          const problemMap = new Map(allProblems.map(p => [p.id, p]));

          // Process pinned problems first (in their pin order)
          collectionPins.forEach((problemId, index) => {
            const problem = problemMap.get(problemId);
            if (problem) {
              pinned.push({ ...problem, sequenceNumber: index + 1 });
            }
          });

          // Process unpinned problems (in sequence order)
          let unpinnedIndex = 1;
          sequence.forEach(problemId => {
            if (!collectionPins.includes(problemId)) {
              const problem = problemMap.get(problemId);
              if (problem) {
                unpinned.push({ ...problem, sequenceNumber: unpinnedIndex++ });
              }
            }
          });

          // Add any remaining problems not in sequence
          allProblems.forEach(problem => {
            if (!sequence.includes(problem.id) && !collectionPins.includes(problem.id)) {
              unpinned.push({ ...problem, sequenceNumber: unpinnedIndex++ });
            }
          });

          setPinnedProblems(pinned);
          setUnpinnedProblems(unpinned);
        }

        // Load completion status
        if (userProfile) {
          try {
            const statuses = await apiService.getAllCompletionStatus();
            setCompletionStatus(statuses);
          } catch (error) {
            console.warn('Failed to load completion statuses:', error);
            setCompletionStatus({});
          }
        }
      } catch (error) {
        console.error('Failed to load collection:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, userProfile, authLoading, pinData.pinnedProblems]);

  // Update sequence numbers when order changes
  const updateSequenceNumbers = useCallback((problems: ProblemWithSequence[]) => {
    return problems.map((p, index) => ({ ...p, sequenceNumber: index + 1 }));
  }, []);

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, problemId: string) => {
    if (!dragEnabled) return;
    setDraggedItem(problemId);
    document.body.classList.add('is-dragging');

    // Set drag effect
    e.dataTransfer.effectAllowed = 'move';

    // Fix for Safari: Set a custom drag image to prevent it from capturing extra elements
    const target = e.currentTarget as HTMLElement;
    if (target) {
      // Create a clone of the dragged element for the drag image
      const dragImage = target.cloneNode(true) as HTMLElement;
      dragImage.style.position = 'absolute';
      dragImage.style.top = '-9999px';
      dragImage.style.left = '-9999px';
      dragImage.style.width = `${target.offsetWidth}px`;
      dragImage.style.opacity = '0.8';
      dragImage.style.background = '#dbeafe';
      dragImage.style.borderRadius = '8px';
      document.body.appendChild(dragImage);

      // Set the custom drag image
      e.dataTransfer.setDragImage(dragImage, e.nativeEvent.offsetX, e.nativeEvent.offsetY);

      // Clean up the temporary element after drag starts
      requestAnimationFrame(() => {
        document.body.removeChild(dragImage);
      });
    }
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent, problemId: string) => {
    e.preventDefault();
    if (!dragEnabled || !draggedItem) return;
    setDragOverItem(problemId);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverItem(null);
    document.body.classList.remove('is-dragging');
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverItem(null);
  };

  // Handle drop for unpinned problems
  const handleDropUnpinned = (targetId: string) => {
    if (!draggedItem || draggedItem === targetId) {
      handleDragEnd();
      return;
    }

    const draggedIndex = unpinnedProblems.findIndex(p => p.id === draggedItem);
    const targetIndex = unpinnedProblems.findIndex(p => p.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      handleDragEnd();
      return;
    }

    const newProblems = [...unpinnedProblems];
    const [removed] = newProblems.splice(draggedIndex, 1);
    newProblems.splice(targetIndex, 0, removed);

    setUnpinnedProblems(updateSequenceNumbers(newProblems));
    setHasChanges(true);
    handleDragEnd();
  };

  // Handle drop for pinned problems
  const handleDropPinned = (targetId: string) => {
    if (!draggedItem || draggedItem === targetId) {
      handleDragEnd();
      return;
    }

    const draggedIndex = pinnedProblems.findIndex(p => p.id === draggedItem);
    const targetIndex = pinnedProblems.findIndex(p => p.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) {
      handleDragEnd();
      return;
    }

    const newProblems = [...pinnedProblems];
    const [removed] = newProblems.splice(draggedIndex, 1);
    newProblems.splice(targetIndex, 0, removed);

    setPinnedProblems(updateSequenceNumbers(newProblems));
    setHasChanges(true);
    handleDragEnd();
  };

  // Move problem up/down in unpinned list
  const moveUnpinned = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= unpinnedProblems.length) return;

    const newProblems = [...unpinnedProblems];
    [newProblems[index], newProblems[newIndex]] = [newProblems[newIndex], newProblems[index]];

    setUnpinnedProblems(updateSequenceNumbers(newProblems));
    setHasChanges(true);
  };

  // Move problem up/down in pinned list
  const movePinned = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= pinnedProblems.length) return;

    const newProblems = [...pinnedProblems];
    [newProblems[index], newProblems[newIndex]] = [newProblems[newIndex], newProblems[index]];

    setPinnedProblems(updateSequenceNumbers(newProblems));
    setHasChanges(true);
  };

  // Quick sort options
  const handleQuickSort = (option: SortOption) => {
    let sorted = [...unpinnedProblems];

    switch (option) {
      case 'difficulty-asc':
        sorted.sort((a, b) => (DIFFICULTY_ORDER[a.difficulty] || 99) - (DIFFICULTY_ORDER[b.difficulty] || 99));
        break;
      case 'difficulty-desc':
        sorted.sort((a, b) => (DIFFICULTY_ORDER[b.difficulty] || 99) - (DIFFICULTY_ORDER[a.difficulty] || 99));
        break;
      case 'leetcode':
        sorted.sort((a, b) => (a.leetcode_number || 0) - (b.leetcode_number || 0));
        break;
      case 'completion':
        sorted.sort((a, b) => {
          const aCompleted = completionStatus[a.id] ? 1 : 0;
          const bCompleted = completionStatus[b.id] ? 1 : 0;
          return aCompleted - bCompleted; // Uncompleted first
        });
        break;
      case 'custom':
        // Reset to original sequence
        const problemMap = new Map(unpinnedProblems.map(p => [p.id, p]));
        const collectionPins = id ? (pinData.pinnedProblems[id] || []) : [];
        sorted = originalSequence
          .filter(pid => !collectionPins.includes(pid))
          .map(pid => problemMap.get(pid))
          .filter((p): p is ProblemWithSequence => p !== undefined);
        break;
    }

    setUnpinnedProblems(updateSequenceNumbers(sorted));
    setHasChanges(true);
  };

  // Save sequence
  const handleSave = async () => {
    if (!collection || !id) return;

    try {
      setSaving(true);

      // Combine pinned and unpinned into final sequence
      const finalSequence = [
        ...pinnedProblems.map(p => p.id),
        ...unpinnedProblems.map(p => p.id)
      ];

      await collectionsApi.updateProblemSequence(id, finalSequence);

      // Also sync the pinnedProblems order so it persists across sessions
      if (pinnedProblems.length > 0) {
        const newPinnedOrder = pinnedProblems.map(p => p.id);
        const updatedPinData: PinData = {
          ...pinData,
          pinnedProblems: {
            ...pinData.pinnedProblems,
            [id]: newPinnedOrder
          },
          lastUpdated: Date.now()
        };
        await pinsApi.updatePins(updatedPinData);
      }

      setOriginalSequence(finalSequence);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save sequence:', error);
      alert('Failed to save sequence. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    navigate(`/collections/${id}`);
  };

  // Get completed count
  const getCompletedCount = () => {
    const allProblems = [...pinnedProblems, ...unpinnedProblems];
    return allProblems.filter(p => completionStatus[p.id]).length;
  };

  if (loading) {
    return (
      <div className="sequence-page">
        <LoadingModal isOpen={true} message="Loading collection..." />
      </div>
    );
  }

  if (!collection) {
    return <div className="error-container">Collection not found</div>;
  }

  // Check ownership
  const isOwner = Boolean(userProfile && collection.ownerId === userProfile.uid);
  const isSystemCollection = collection.ownerId === 'System';

  return (
    <div className="sequence-page">
      {/* Back Link */}
      <button type="button" className="back-link" onClick={handleCancel}>
        <span>←</span> Back to Collection
      </button>

      {/* Page Header */}
      <div className="sequence-header">
        <div className="sequence-header-left">
          <h1 className="sequence-title">Manage Sequence: {collection.name}</h1>
          <p className="sequence-subtitle">
            {isSystemCollection
              ? "View problem order (system collections are read-only)"
              : "Drag and drop or edit sequence numbers to reorder problems"}
          </p>
        </div>
        {isSystemCollection && (
          <span className="system-badge">System Collection</span>
        )}
      </div>

      {/* Stats */}
      <div className="sequence-meta">
        <span>{pinnedProblems.length + unpinnedProblems.length} problems</span>
        <span className="separator">•</span>
        <span className="completion-summary">
          <span className="completion-bar">
            <span
              className="completion-fill"
              style={{ width: `${(getCompletedCount() / (pinnedProblems.length + unpinnedProblems.length)) * 100}%` }}
            />
          </span>
          {getCompletedCount()} completed ({Math.round((getCompletedCount() / (pinnedProblems.length + unpinnedProblems.length)) * 100) || 0}%)
        </span>
        <span className="separator">•</span>
        <span>Last updated {formatFirestoreDate(collection.updatedAt)}</span>
      </div>

      {/* Info Box */}
      {!isSystemCollection && (
        <div className="info-box">
          <span className="info-icon">💡</span>
          <div className="info-content">
            <strong>Tip:</strong> Pinned problems always appear at the top. Drag problems using the handle (⋮⋮) to reorder within pinned or unpinned sections.
            Changes are saved when you click "Save Order".
          </div>
        </div>
      )}

      {/* Quick Reorder Options */}
      {isOwner && !isSystemCollection && (
        <div className="quick-reorder">
          <span className="quick-reorder-label">Quick sort unpinned by:</span>
          <button className="quick-btn" onClick={() => handleQuickSort('difficulty-asc')}>
            Difficulty (Easy → Hard)
          </button>
          <button className="quick-btn" onClick={() => handleQuickSort('difficulty-desc')}>
            Difficulty (Hard → Easy)
          </button>
          <button className="quick-btn" onClick={() => handleQuickSort('leetcode')}>
            LeetCode Number
          </button>
          <button className="quick-btn" onClick={() => handleQuickSort('completion')}>
            Completion Status
          </button>
          <button className="quick-btn" onClick={() => handleQuickSort('custom')}>
            Reset to Default
          </button>
        </div>
      )}

      {/* Action Bar */}
      {isOwner && !isSystemCollection && (
        <div className="action-bar">
          <div className="action-left">
            <div className="drag-mode-toggle">
              <span>Drag mode</span>
              <div
                className={`toggle-switch ${dragEnabled ? 'active' : ''}`}
                onClick={() => setDragEnabled(!dragEnabled)}
              />
            </div>
          </div>
          <div className="action-right">
            <button className="btn btn-ghost" onClick={handleCancel}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!hasChanges || saving}
            >
              {saving ? 'Saving...' : 'Save Order'}
            </button>
          </div>
        </div>
      )}

      {/* Problem Lists */}
      <div className="problem-list">
        {/* List Header */}
        <div className="list-header">
          <span style={{ textAlign: 'center' }}>#</span>
          <span style={{ textAlign: 'center' }}>Done</span>
          <span>Problem</span>
          <span>LeetCode</span>
          <span>Difficulty</span>
          <span style={{ textAlign: 'center' }}>Move</span>
        </div>

        {/* Pinned Section */}
        {pinnedProblems.length > 0 && (
          <>
            <div className="section-header pinned-section">
              <span className="pin-icon">📌</span> Pinned Problems ({pinnedProblems.length})
            </div>
            {pinnedProblems.map((problem, index) => (
              <div
                key={problem.id}
                className={`problem-item ${draggedItem === problem.id ? 'dragging' : ''} ${dragOverItem === problem.id ? 'drag-over' : ''}`}
                draggable={dragEnabled && isOwner && !isSystemCollection}
                onDragStart={(e) => handleDragStart(e, problem.id)}
                onDragOver={(e) => handleDragOver(e, problem.id)}
                onDragLeave={handleDragLeave}
                onDragEnd={handleDragEnd}
                onDrop={() => handleDropPinned(problem.id)}
              >
                <div className="seq-number">
                  <span className={`drag-handle ${!(isOwner && !isSystemCollection && dragEnabled) ? 'disabled' : ''}`}>⋮⋮</span>
                  <span className="seq-display">{problem.sequenceNumber}</span>
                </div>
                <div className="checkbox-wrapper">
                  <div className={`checkbox ${completionStatus[problem.id] ? 'checked' : ''}`} />
                </div>
                <div className="problem-title">
                  <span className="title-text">{problem.title}</span>
                </div>
                <span className="leetcode-num">#{problem.leetcode_number}</span>
                <div className="tags">
                  <span className={`tag tag-${problem.difficulty.toLowerCase()}`}>
                    {problem.difficulty}
                  </span>
                </div>
                <div className="move-buttons">
                  {isOwner && !isSystemCollection && (
                    <>
                      <button
                        className="move-btn"
                        onClick={() => movePinned(index, 'up')}
                        disabled={index === 0}
                      >
                        ▲
                      </button>
                      <button
                        className="move-btn"
                        onClick={() => movePinned(index, 'down')}
                        disabled={index === pinnedProblems.length - 1}
                      >
                        ▼
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Unpinned Section */}
        {unpinnedProblems.length > 0 && (
          <>
            {pinnedProblems.length > 0 && (
              <div className="section-header unpinned-section">
                Other Problems ({unpinnedProblems.length})
              </div>
            )}
            {unpinnedProblems.map((problem, index) => (
              <div
                key={problem.id}
                className={`problem-item ${draggedItem === problem.id ? 'dragging' : ''} ${dragOverItem === problem.id ? 'drag-over' : ''}`}
                draggable={dragEnabled && isOwner && !isSystemCollection}
                onDragStart={(e) => handleDragStart(e, problem.id)}
                onDragOver={(e) => handleDragOver(e, problem.id)}
                onDragLeave={handleDragLeave}
                onDragEnd={handleDragEnd}
                onDrop={() => handleDropUnpinned(problem.id)}
              >
                <div className="seq-number">
                  <span className={`drag-handle ${!(isOwner && !isSystemCollection && dragEnabled) ? 'disabled' : ''}`}>⋮⋮</span>
                  <span className="seq-display">{problem.sequenceNumber}</span>
                </div>
                <div className="checkbox-wrapper">
                  <div className={`checkbox ${completionStatus[problem.id] ? 'checked' : ''}`} />
                </div>
                <div className="problem-title">
                  <span className="title-text">{problem.title}</span>
                </div>
                <span className="leetcode-num">#{problem.leetcode_number}</span>
                <div className="tags">
                  <span className={`tag tag-${problem.difficulty.toLowerCase()}`}>
                    {problem.difficulty}
                  </span>
                </div>
                <div className="move-buttons">
                  {isOwner && !isSystemCollection && (
                    <>
                      <button
                        className="move-btn"
                        onClick={() => moveUnpinned(index, 'up')}
                        disabled={index === 0}
                      >
                        ▲
                      </button>
                      <button
                        className="move-btn"
                        onClick={() => moveUnpinned(index, 'down')}
                        disabled={index === unpinnedProblems.length - 1}
                      >
                        ▼
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Unsaved Changes Banner */}
      {hasChanges && isOwner && !isSystemCollection && (
        <div className="unsaved-banner">
          <span className="unsaved-text">You have <strong>unsaved changes</strong></span>
          <button className="btn btn-ghost" onClick={() => window.location.reload()}>Discard</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Order'}
          </button>
        </div>
      )}

      <LoadingModal isOpen={saving} message="Saving sequence..." />
    </div>
  );
};
